// A firmware approval's browser-side transport correlation. The boat's
// `firmware_command_status` beat remains the only outcome authority; this
// record merely keeps the POST's idempotency key across route changes and
// reloads so the next render can match that authoritative answer.

export interface FirmwareUpdateAttempt {
  commandKey: string;
  targetVersion: string;
  startedAt: number;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function browserStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function firmwareUpdateAttemptKey(boatId: string): string {
  return `nm:firmware-update-attempt:${boatId}`;
}

export function readFirmwareUpdateAttempt(
  boatId: string,
  storage: StorageLike | null = browserStorage()
): FirmwareUpdateAttempt | null {
  if (storage === null) return null;
  try {
    const value = JSON.parse(
      storage.getItem(firmwareUpdateAttemptKey(boatId)) ?? 'null'
    ) as unknown;
    if (typeof value !== 'object' || value === null) return null;
    const attempt = value as Record<string, unknown>;
    if (
      typeof attempt.commandKey !== 'string' ||
      attempt.commandKey.length === 0 ||
      typeof attempt.targetVersion !== 'string' ||
      !/^\d+\.\d+\.\d+$/.test(attempt.targetVersion) ||
      typeof attempt.startedAt !== 'number' ||
      !Number.isFinite(attempt.startedAt) ||
      attempt.startedAt <= 0
    ) {
      return null;
    }
    return {
      commandKey: attempt.commandKey,
      targetVersion: attempt.targetVersion,
      startedAt: attempt.startedAt,
    };
  } catch {
    return null;
  }
}

export function rememberFirmwareUpdateAttempt(
  boatId: string,
  attempt: FirmwareUpdateAttempt,
  storage: StorageLike | null = browserStorage()
): void {
  if (storage === null) return;
  try {
    storage.setItem(firmwareUpdateAttemptKey(boatId), JSON.stringify(attempt));
  } catch {
    // Storage can be disabled. The boat-reported accepted beat still restores
    // the global in-flight state; only the short pre-accept correlation is lost.
  }
}

export function forgetFirmwareUpdateAttempt(
  boatId: string,
  storage: StorageLike | null = browserStorage()
): void {
  if (storage === null) return;
  try {
    storage.removeItem(firmwareUpdateAttemptKey(boatId));
  } catch {
    /* a stale record is bounded by the shared lifecycle timeout */
  }
}
